/**
 * Minimal WebAuthn helpers. Prefers the modern
 * PublicKeyCredential.parseCreationOptionsFromJSON / parseRequestOptionsFromJSON
 * static methods, with a manual base64url fallback for browsers that don't
 * implement them yet.
 */

function base64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

type CreationOptionsJSON = Record<string, unknown>;
type RequestOptionsJSON = Record<string, unknown>;

function hasParseCreationOptions(
  ctor: unknown,
): ctor is { parseCreationOptionsFromJSON(json: CreationOptionsJSON): PublicKeyCredentialCreationOptions } {
  return typeof ctor === "function" && "parseCreationOptionsFromJSON" in ctor;
}

function hasParseRequestOptions(
  ctor: unknown,
): ctor is { parseRequestOptionsFromJSON(json: RequestOptionsJSON): PublicKeyCredentialRequestOptions } {
  return typeof ctor === "function" && "parseRequestOptionsFromJSON" in ctor;
}

interface DescriptorJSON {
  id: string;
  [key: string]: unknown;
}

function manualCreationOptions(json: CreationOptionsJSON): PublicKeyCredentialCreationOptions {
  const user = json.user as { id: string; [key: string]: unknown };
  const excludeCredentials = (json.excludeCredentials as DescriptorJSON[] | undefined) ?? [];
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge as string),
    user: { ...user, id: base64urlToBuffer(user.id) },
    excludeCredentials: excludeCredentials.map((c) => ({ ...c, id: base64urlToBuffer(c.id) })),
  } as PublicKeyCredentialCreationOptions;
}

function manualRequestOptions(json: RequestOptionsJSON): PublicKeyCredentialRequestOptions {
  const allowCredentials = (json.allowCredentials as DescriptorJSON[] | undefined) ?? [];
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge as string),
    allowCredentials: allowCredentials.map((c) => ({ ...c, id: base64urlToBuffer(c.id) })),
  } as PublicKeyCredentialRequestOptions;
}

function credentialToJSON(credential: PublicKeyCredential): unknown {
  const anyCredential = credential as unknown as { toJSON?: () => unknown };
  if (typeof anyCredential.toJSON === "function") return anyCredential.toJSON();

  const response = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: response.attestationObject ? bufferToBase64url(response.attestationObject) : undefined,
      authenticatorData: response.authenticatorData ? bufferToBase64url(response.authenticatorData) : undefined,
      signature: response.signature ? bufferToBase64url(response.signature) : undefined,
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

export async function createPasskey(optionsJSON: CreationOptionsJSON): Promise<unknown> {
  const options = hasParseCreationOptions(PublicKeyCredential)
    ? PublicKeyCredential.parseCreationOptionsFromJSON(optionsJSON)
    : manualCreationOptions(optionsJSON);
  const credential = (await navigator.credentials.create({ publicKey: options })) as PublicKeyCredential | null;
  if (!credential) throw new Error("passkey_creation_cancelled");
  return credentialToJSON(credential);
}

export async function getPasskey(optionsJSON: RequestOptionsJSON): Promise<unknown> {
  const options = hasParseRequestOptions(PublicKeyCredential)
    ? PublicKeyCredential.parseRequestOptionsFromJSON(optionsJSON)
    : manualRequestOptions(optionsJSON);
  const credential = (await navigator.credentials.get({ publicKey: options })) as PublicKeyCredential | null;
  if (!credential) throw new Error("passkey_assertion_cancelled");
  return credentialToJSON(credential);
}

export function isWebAuthnSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window;
}
